import { useCallback, useState, useEffect, useRef } from 'react';
import { Box, Stack, FormLabel } from '@chakra-ui/react';

import { TextEditor } from '@/components/index';
import AutoFillMultiSelect from '@/components/presentational/ChipInsertPalette';

import { formLabelStyling } from 'components/forms/new/formStyles';
import walkthroughIds from '@/pages/organisations/walkthroughIds';

const { billing } = walkthroughIds.companies;
import { useSelector } from 'react-redux';

// component to determine the way to format the fields for the invoice
const InvoiceFormatFields = ({
    fields,
    format,
    globalCursorPosition,
    setGlobalCursorPosition,
    billingState,
    setBillingState,
}) => {
    const { darkMode } = useSelector((state) => state.settings);

    // Local state for instant typing feedback
    const [localValue, setLocalValue] = useState(
        billingState[format.value] || '',
    );
    const isTypingRef = useRef(false);
    const debounceRef = useRef(null);

    // Sync from parent when billingState changes externally (e.g., chip clicks)
    useEffect(() => {
        if (!isTypingRef.current) {
            setLocalValue(billingState[format.value] || '');
        }
    }, [billingState, format.value]);

    // Cancels any pending debounced write so it can't later overwrite this chip insert with stale text.
    const handleChipInsert = useCallback(
        (payload) => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
                debounceRef.current = null;
            }
            isTypingRef.current = false;
            setBillingState(payload);
        },
        [setBillingState],
    );

    const handleSetCursorPosition = useCallback(
        (value) => {
            setGlobalCursorPosition((prev) => ({
                ...prev,
                [format.value]: value,
            }));
        },
        [setGlobalCursorPosition, format.value],
    );

    const handleChange = useCallback(
        (e) => {
            const value = e && e.target ? e.target.value : e;
            // Update local state immediately for instant feedback
            setLocalValue(value);
            isTypingRef.current = true;

            // Debounce the parent state update
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
            debounceRef.current = setTimeout(() => {
                setBillingState({
                    [format.value]: value,
                });
                isTypingRef.current = false;
            }, 150);
        },
        [format.value, setBillingState],
    );

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    return (
        <Box key={format.value} sx={{ flexGrow: 1, marginTop: 4 }}>
            <Stack spacing={4}>
                {billingState[fields?.value]?.length > 0 && (
                    <Box>
                        <FormLabel
                            sx={{
                                ...formLabelStyling(darkMode),
                                paddingTop: '0px',
                            }}>
                            {fields.label}
                        </FormLabel>
                        <AutoFillMultiSelect
                            staticEnum={
                                billingState[fields?.value]?.length > 0
                                    ? billingState[fields?.value]
                                          .split(',')
                                          .map((v) => ({
                                              id: '[' + v.trim() + ']',
                                              name: v.trim(),
                                          }))
                                    : []
                            }
                            activeTextareaID={format.value}
                            activeTextareaField={format.value}
                            setState={handleChipInsert}
                            label=""
                            setCursorPosition={handleSetCursorPosition}
                            cursorPosition={
                                globalCursorPosition[format.value] ?? 0
                            }
                            data-walkthroughid={`${billing.ufAccordionSelect}/${format.value}`}
                        />
                    </Box>
                )}
                <Box>
                    <FormLabel
                        sx={{
                            ...formLabelStyling(darkMode),
                            paddingTop: '0px',
                        }}>
                        {format.label}
                    </FormLabel>
                    <TextEditor
                        id={format.value}
                        setCursorPosition={handleSetCursorPosition}
                        textfield={{
                            value: localValue,
                            multiline: false,
                            onChange: handleChange,
                        }}
                        dataWalkthroughId={`${billing.ufAccordionInput}/${format.value}`}
                    />
                </Box>
            </Stack>
        </Box>
    );
};

export default InvoiceFormatFields;
